import { css } from '@emotion/react';
import { IonButton, IonContent, IonIcon, IonModal } from '@ionic/react';
import { arrowUndoOutline, globeOutline } from 'ionicons/icons';
import React from 'react';
import { useLocation } from '../../contexts/LocationContext';
import { useSettings } from '../../contexts/SettingsContext';
import { Viewport } from '../../shared/viewport';
import { MapSettingsDto } from '../../types';
import ModalHeader from '../ModalHeader';

interface ExploreFurtherProps {
  delayMs: number;
}

const ExploreFurther: React.FC<ExploreFurtherProps> = ({ delayMs }) => {
  const { network, mapSettings } = useSettings();
  const { viewport, setViewport } = useLocation();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isOOB, setIsOOB] = React.useState<boolean>(false);
  const [needToCheckOOB, setNeedToCheckOOB] = React.useState(false);

  const checkIsOOB = (viewport: Viewport, mapSettings: MapSettingsDto) => {
    const { longitude: centerLng, latitude: centerLat } = viewport;
    const { bbox } = mapSettings;
    const [[minLng, minLat], [maxLng, maxLat]] = bbox!;
    return centerLng < minLng || centerLng > maxLng || centerLat < minLat || centerLat > maxLat;
  };

  // Whenever the viewport moves, set the need to check OOB
  React.useEffect(() => {
    setNeedToCheckOOB(true);
  }, [viewport]);

  // Throttled check for out of bounds
  React.useEffect(() => {
    if (isOpen || !viewport || !mapSettings?.bbox || !needToCheckOOB) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsOOB(checkIsOOB(viewport, mapSettings));
      setNeedToCheckOOB(false);
    }, 500);
    return () => clearInterval(timeoutId);
  }, [needToCheckOOB, isOpen, viewport, mapSettings, delayMs]);

  // Schedule a timeout for when the modal is supposed to open
  React.useEffect(() => {
    if (isOpen || needToCheckOOB || !isOOB) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsOpen(true);
      setIsOOB(true);
      setNeedToCheckOOB(true);
      return;
    }, delayMs);
    return () => clearInterval(timeoutId);
  }, [needToCheckOOB, isOpen, delayMs, isOOB]);

  if (!network || !mapSettings) {
    return null;
  }

  const exploreFurtherCss = {
    modal: [
      css({
        '--max-height': '200px',
        '--max-width': '420px',
      }),
    ],
    header: [
      css({
        backgroundColor: 'var(--ion-color-secondary)',
      }),
    ],
    content: [
      css({
        textAlign: 'center',
        padding: '1rem',
        p: { fontSize: '1.2rem' },
      }),
    ],
    buttons: [
      css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '1rem',
        gap: '1rem',
        width: '100%',
        '& ion-button': {
          width: '80%', // Makes buttons a consistent width
        },
      }),
    ],
  };

  const { name: networkName } = network;
  const {
    center: [networkLng, networkLat],
    zoom: networkZoom,
  } = mapSettings;
  return (
    <IonModal isOpen={isOpen} css={exploreFurtherCss.modal}>
      <ModalHeader onClose={() => setIsOpen(false)} title="Want to explore further?" />
      <IonContent>
        <div css={exploreFurtherCss.buttons}>
          <IonButton href="/world" color="primary" fill="solid">
            <IonIcon slot="start" icon={globeOutline} />
            View World Map
          </IonButton>
          <IonButton
            onClick={() => {
              setViewport({ ...viewport!, latitude: networkLat, longitude: networkLng, zoom: networkZoom });
              setIsOpen(false);
            }}
            fill="solid"
            color="light"
          >
            <IonIcon slot="start" icon={arrowUndoOutline} />
            Back to {networkName}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default ExploreFurther;
